import { Stream } from '@cloudflare/stream-react';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAttachmentDetails } from 'api';
import { FileItem } from 'api/types';
import { theme } from 'styles';
import { handleFileDownload } from 'utils';

type Props = {
  file: FileItem;
  open: boolean;
  onClose: () => void;
};

export const NoPreviewWrapper = styled('div')`
  padding: 32px;
  text-align: center;
  color: ${({ theme }) => theme.palette.secondary.dark};
`;

export const DownloadButton = styled(Button)`
  text-transform: none;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  border-radius: 8px;
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.palette.info.main};
`;

export const FilePreviewModal = ({ file, open, onClose }: Props) => {
  const { t } = useTranslation();
  const fileUrl = file.file_url.original || file.file_url.large || `/files/${file.id}`;

  const {
    data: blobUrl,
    isLoading,
    refetch,
  } = useAttachmentDetails({
    attachmentId: file.id,
    fileName: file.display_name,
    fileUrl,
    enabled: open,
  });

  const handleDownload = async () => {
    const { data } = await refetch();
    if (data?.data) {
      await handleFileDownload({ blobFile: data.data, fileName: file.display_name });
    }
  };

  const renderContent = () => {
    if (isLoading || !blobUrl) {
      return (
        <div
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}
        >
          <CircularProgress />
        </div>
      );
    }

    if (file.type === 'image') {
      return (
        <img
          src={blobUrl}
          alt={file.display_name}
          style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }}
        />
      );
    }

    if (file.type === 'video') {
      return (
        <Stream
          key={file.id}
          controls
          autoplay
          poster={`https://customer-${process.env.REACT_APP_CLOUDFLARE_STREAM_ACCOUNT_ID}.cloudflarestream.com/${file.file_url.video_uid}/thumbnails/thumbnail.jpg`}
          src={file.file_url.video_uid}
          onLoadedData={() => null}
          height="720px"
          width="100%"
          responsive={false}
        />
      );
    }
    if (file.type === 'document') {
      const ext = file.file_extension?.toLowerCase();
      if (ext === '.pdf') {
        return (
          <iframe
            src={blobUrl}
            title={file.display_name}
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        );
      }
      return <NoPreviewWrapper>{t('common.noPreviewAvailable')}</NoPreviewWrapper>;
    }

    return <NoPreviewWrapper>No preview available for this file type.</NoPreviewWrapper>;
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { width: '100%', maxWidth: 800 } }}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 1,
        }}
      >
        {file.display_name}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ p: file.type === 'image' ? 0 : 2, display: 'flex', justifyContent: 'center' }}
      >
        {renderContent()}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <DownloadButton
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={isLoading}
          style={{
            height: '48px',
            padding: '0 32px',
            backgroundColor: theme.palette.info.main,
            color: '#fff',
            borderRadius: '8px',
            fontWeight: 400,
            textTransform: 'none',
          }}
        >
          Download file
        </DownloadButton>
      </DialogActions>
    </Dialog>
  );
};
